import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface Toast {
    id: string;
    type: ToastType;
    message: string;
    duration?: number;
}

interface ToastContextType {
    showToast: (message: string, type?: ToastType, duration?: number) => void;
    success: (message: string, duration?: number) => void;
    error: (message: string, duration?: number) => void;
    warning: (message: string, duration?: number) => void;
    info: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within ToastProvider");
    }
    return context;
}

export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((message: string, type: ToastType = "info", duration = 5000) => {
        const id = Math.random().toString(36).substring(7);
        const newToast: Toast = { id, type, message, duration };

        setToasts((prev) => [...prev, newToast]);

        if (duration > 0) {
            setTimeout(() => {
                setToasts((prev) => prev.filter((t) => t.id !== id));
            }, duration);
        }
    }, []);

    const success = useCallback((message: string, duration?: number) => showToast(message, "success", duration), [showToast]);
    const error = useCallback((message: string, duration?: number) => showToast(message, "error", duration), [showToast]);
    const warning = useCallback((message: string, duration?: number) => showToast(message, "warning", duration), [showToast]);
    const info = useCallback((message: string, duration?: number) => showToast(message, "info", duration), [showToast]);

    const removeToast = (id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    };

    const getToastStyles = (type: ToastType) => {
        const baseStyles = {
            padding: "16px 20px",
            borderRadius: "8px",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            boxShadow: "var(--toast-box-shadow)",
            animation: "slideIn 0.3s ease-out",
            fontWeight: 600,
        };

        const typeStyles = {
            success: {
                background: "var(--surface-elevated)",
                color: "var(--success)",
                border: "2px solid var(--success)",
            },
            error: {
                background: "var(--surface-elevated)",
                color: "var(--error)",
                border: "2px solid var(--error)",
            },
            warning: {
                background: "var(--surface-elevated)",
                color: "var(--warning)",
                border: "2px solid var(--warning)",
            },
            info: {
                background: "var(--surface-elevated)",
                color: "var(--focus-ring)",
                border: "2px solid var(--focus-ring)",
            },
        };

        return { ...baseStyles, ...typeStyles[type] };
    };

    const getIcon = (type: ToastType) => {
        const icons = {
            success: "?",
            error: "?",
            warning: "!",
            info: "i",
        };
        return icons[type];
    };

    return (
        <ToastContext.Provider value={{ showToast, success, error, warning, info }}>
            {children}
            <div className="toast-container">
                {toasts.map((toast) => (
                    <div key={toast.id} className="toast-item" style={getToastStyles(toast.type)}>
                        <span style={{ fontSize: "1.25rem", width: 22, textAlign: "center" }}>{getIcon(toast.type)}</span>
                        <span style={{ flex: 1 }}>{toast.message}</span>
                        <button onClick={() => removeToast(toast.id)} className="toast-close" aria-label="Zatvori">×</button>
                    </div>
                ))}
            </div>
            <style>
                {`
                    @keyframes slideIn {
                        from {
                            transform: translateX(100%);
                            opacity: 0;
                        }
                        to {
                            transform: translateX(0);
                            opacity: 1;
                        }
                    }
                `}
            </style>
        </ToastContext.Provider>
    );
}
